import styled, { css } from "styled-components";

export const StyledEditorBar = styled.div`
    display: flex;
    margin: 0 -20px;
    margin-top: 15px;
    background-color: ${p => p.theme.c.boost};
    box-shadow: 0 0 10px 0 ${p => p.theme.c.blackShadow3};
    height: 50px;
    padding: 0 8px;
    position: relative;

    ${p =>
        p.theme.sm.max(css`
            flex-direction: column;
            min-height: 150px;
            height: auto;
        `)}
`;

export const CenterTooltip = styled.div`
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 16px;
    border-radius: 4px;
    box-shadow: 0 0 4px 0 ${p => p.theme.c.blackShadow3};
    padding: 8px;
    font-weight: 300;
    font-size: 15px;
    background-color: ${p => p.theme.c.snow};
    color: ${p => p.theme.c.dark};
    width: 160px;
    &:before {
        content: "";
        width: 10px;
        height: 10px;
        transform: rotate(45deg);
        position: absolute;
        border-radius: 2px;
        bottom: -4px;
        left: calc(50% - 4px);
        background-color: ${p => p.theme.c.snow};
    }
`;

export const StyledCenter = styled.button`
    margin: 9px;
    padding: 2px 8px 4px;
    font-size: 18px;
    height: 32px;
    position: relative;

    ${p =>
        p.theme.sm.max(css`
            height: 32px;
            display: block;
        `)}

    ${p =>
        p.disabled
            ? css`
                  cursor: default;

                  &.button.button--pretty {
                      color: ${p => p.theme.c.disabled} !important;
                  }
              `
            : undefined}

    &.Transparent {
        &,
        &:hover {
            background-color: transparent;
            ${p =>
        !p.disabled &&
        css`
                    color: ${p => p.theme.c.snowOnActive} !important;
                `}
        }
    }

    ${CenterTooltip} {
        display: none;

    }
    &:hover {
        ${CenterTooltip} {
            display: block;
        }
    }
`;

const styledFlex = styled.div`
    display: flex;
    align-items: center;
    ${p =>
        p.theme.sm.max(css`
            height: 50px;

            justify-content: left;
        `)}

    ${p =>
        p.theme.xs(css`
            .prop-label {
                display: none;
            }
        `)}
`;

export const StyledFlexLeft = styled(styledFlex) <{ flexDirection?: string }>`
    flex: 1;
    ${p =>
        p.flexDirection === "column" &&
        p.theme.xs(css`
            flex-direction: column;
            height: auto;
        `)}
`;

export const StyledFlexRight = styled(styledFlex)`
    flex: 1;
    justify-content: flex-end;
`;

export const StyledChanger = styled.div`
    ${p =>
        p.theme.md.min(css`
            position: relative;
        `)}
    display: flex;
    height: 100%;
    align-items: center;
    font-weight: 600;

    line-height: 18px;

    .ColorRect {
        width: 24px;
        height: 24px;
        border-radius: 7px;
        transition: border-color 200ms;
        box-sizing: border-box;
        
        &.ColorRectBase {
            border: ${p => p.theme.c.dark} solid 2px;
        }

        &.ColorRectPopup {
            margin: 8px;
            display: inline-block;
            cursor: pointer;
            transition: 200ms;

            &:hover {
                opacity: 0.7;
            }
        }
    }

    .ColorChanger {
        display: flex;
        align-items: center;

        .Label {
            margin-right: 8px;
        }

        > div {
            display: inline-block;
        }

        &:hover {
            .ColorRect {
                border: 2px ${p => p.theme.c.pale} solid !important;
                box-sizing: border-box;
            }
        }
    }

    > div:first-of-type {
        color: ${p => p.theme.c.dark};
        padding: 0 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: 200ms;

        margin-bottom: 0;
        [class^="i-"] {
            font-size: 40px;
        }

        &:hover {
            color: ${p => p.theme.c.pale};
        }
    }

    &.Active > div:first-of-type {
        &,
        &:hover {
            color: ${p => p.theme.c.whiteOnActive};
        }
    }

    &.Active {
        .ColorChanger {
            .ColorRect {
                &,
                &:hover {
                    border: 2px ${p => p.theme.c.whiteOnActive} solid !important;
                    box-sizing: border-box;
                }
            }
        }
    }

    i {
        font-style: normal;
        font-weight: 600;
    }

    &.Disabled {
        pointer-events: none;
        opacity: 0.6;
    }
`;

export const StyledChangerPopup = styled.div`
    position: absolute;
    background-color: ${p => p.theme.c.white};
    bottom: calc(100% + 8px);
    border: ${p => p.theme.c.boost} 1px solid;
    border-radius: 10px;
    left: 8px;
    padding: 12px 16px 0px;
    min-width: 120px;

    ${p =>
        p.theme.xs(css`
            left: calc((100vw - 234px) / 2);
        `)}

    ${p =>
        p.theme.sm.only(css`
            left: calc((${p.theme.$screenSm}px - 234px) / 2);
        `)}

    &.Dark {
        padding: 0;
        background-color: ${p => p.theme.c.boost};
        border-color: ${p => p.theme.c.dark};
    }

    &.OnRight {
        ${p =>
        p.theme.md.min(css`
                left: auto;
                right: 8px;
                text-align: left;
            `)}
    }
`;

export const NewMarker = styled.div`
    background-color: ${p => p.theme.c.play};
    color: ${p => p.theme.c.snow} !important;
    position: absolute;
    top: -28px;
    padding: 8px !important;
    border-radius: 8px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 15px;
    ${p =>
        p.theme.sm.max(css`
            display: none;
        `)}
    &::before {
        content: "";
        position: absolute;
        width: 8px;
        height: 8px;
        transform: rotate(45deg);
        border-radius: 2px;
        background-color: ${p => p.theme.c.play};
        top: 29px;
        left: 50%;
        margin-left: -4px;
    }
`;
